import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { SeatMapComponent } from 'src/app/components/seat-map/seat-map.component';
import { ActivatedRoute, Router } from '@angular/router';
import {
  faArrowLeft, faCalendarAlt, faTicket, faCheckCircle, faChair,
  faTrashAlt, faSearch, faSliders, faTimes, faCircleCheck, faTag, faPlus, faUsers
} from '@fortawesome/pro-light-svg-icons';
import { NgbCalendar, NgbDate, NgbDateParserFormatter, NgbDatepickerNavigateEvent, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { formatDate, getLocaleFirstDayOfWeek } from '@angular/common';
import { PassesService } from 'src/app/services/passes.service';
import { CartService } from 'src/app/services/cart.service';
import { Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AppSettings } from 'src/app/app-settings';
import {
  AppearanceAnimation,
  DialogInitializer,
  DialogLayoutDisplay,
  DisappearanceAnimation,
} from '@costlydeveloper/ngx-awesome-popup';
import { DialogSelectPaymentComponent } from 'src/app/components/dialog-select-payment/dialog-select-payment.component';
import { DialogPayComponent } from 'src/app/components/dialog-pay/dialog-pay.component';
import { DialogErrorBondsComponent } from 'src/app/components/dialog-error-bonds/dialog-error-bonds.component';
import { TicketsEventService } from 'src/app/services/tickets-event.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogGenericComponent } from 'src/app/components/dialog-generic/dialog-generic.component';
import { SingleSeatPassOfferService } from 'src/app/services/single-seat-pass-offer.service';
import { isFixedPricePass } from 'src/app/helpers/fixed-price-pass';

@Component({
  selector: 'app-pass-sale-bulk',
  templateUrl: './pass-sale-bulk.component.html',
  styleUrls: ['./pass-sale-bulk.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PassSaleBulkComponent implements OnInit, OnDestroy {
  pass: any = null;
  loading: boolean = true;
  passId: number;
  quantity: number = 1;

  // Current event being viewed in center panel
  currentEventId: number | null = null;

  // Local selections: Map<eventId, cartLine[]> — one array entry per "slot"
  eventSelections: Map<number, any[]> = new Map();
  selectionDetails: Map<number, any[]> = new Map();
  eventDetailsList: { eventId: number; details: any[]; completed: boolean }[] = [];

  allowedSessionsByEvent: Map<number, number[] | null> = new Map();
  private deduplicatedPassItems: any[] = [];

  submitting: boolean = false;
  passAdded: boolean = false;

  panelCollapsed: boolean = false;
  private routeSubscription: Subscription;
  private collapsedSub: Subscription;

  faArrowLeft = faArrowLeft;
  faCalendar = faCalendarAlt;
  faTicket = faTicket;
  faCheck = faCheckCircle;
  faChair = faChair;
  faTrash = faTrashAlt;
  faSearch = faSearch;
  faSliders = faSliders;
  faTimes = faTimes;
  faCircleCheck = faCircleCheck;
  faTag = faTag;
  faPlus = faPlus;
  faUsers = faUsers;
  urlImages = AppSettings.API_EVENTS_ASSETS;

  // Filters
  filtersOpen: boolean = false;
  searchText: string = '';
  stringDate: string = '';
  openDatePicker: boolean = false;
  fromDate!: any;
  toDate!: any;
  hoveredDate!: any;
  currentMonth: number;
  minDate: NgbDateStruct;
  model: NgbDateStruct;
  @ViewChild('fechaDropdown') fechaDropdown!: ElementRef;
  @ViewChild(SeatMapComponent) seatMap?: SeatMapComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private passesService: PassesService,
    private cartService: CartService,
    private ticketsEventService: TicketsEventService,
    private calendar: NgbCalendar,
    public formatter: NgbDateParserFormatter,
    private dialog: MatDialog,
    private sameSeatOffer: SingleSeatPassOfferService,
  ) {
    this.model = this.calendar.getToday();
    this.minDate = { day: 1, month: 0, year: 2020 };
    this.currentMonth = this.model.month;
  }

  ngOnInit(): void {
    this.collapsedSub = this.cartService.cartCollapsed$.subscribe(v => this.panelCollapsed = v);
    this.routeSubscription = this.route.params.subscribe(params => {
      this.passId = +params['idPass'];
      this.quantity = +params['quantity'] || 1;
      const idEvent = params['idEvent'];
      this.currentEventId = idEvent ? +idEvent : null;

      if (!this.pass || this.pass.id !== this.passId) {
        this.loadPass(this.passId);
      }
    });
  }

  loadPass(id: number) {
    this.loading = true;
    this.passesService.getPassDetail(id).subscribe({
      next: (pass) => {
        if (pass?.single_seat) {
          // Un abono de butaca única no tiene venta evento a evento: va a su pantalla
          this.router.navigate(['/pass-sale', this.passId], { queryParams: { quantity: this.quantity }, replaceUrl: true });
          return;
        }
        this.pass = pass;
        this.buildDeduplicatedItems();
        this.loading = false;
        if (!this.currentEventId) {
          const items = this.getPassItems();
          if (items.length > 0) {
            const firstEvent = this.getEvent(items[0]);
            this.router.navigate(
              ['/pass-sale-bulk', this.passId, this.quantity, 'event', firstEvent.id],
              { replaceUrl: true }
            );
          }
        }
      },
      error: () => { this.loading = false; }
    });
  }

  private buildDeduplicatedItems() {
    const raw = this.pass?.pass_items || this.pass?.passItems || [];
    const byEvent = new Map<number, any[]>();
    for (const item of raw) {
      const event = item.event || item;
      const eid: number = item.event_id ?? event.id;
      if (!byEvent.has(eid)) byEvent.set(eid, []);
      byEvent.get(eid)!.push(item);
    }
    this.allowedSessionsByEvent = new Map();
    byEvent.forEach((items, eventId) => {
      const hasNull = items.some((i: any) => !i.session_id);
      this.allowedSessionsByEvent.set(eventId, hasNull ? null : items.map((i: any) => i.session_id));
    });
    this.deduplicatedPassItems = Array.from(byEvent.values()).map(items => items[0]);
  }

  getPassItems(): any[] {
    return this.deduplicatedPassItems.length ? this.deduplicatedPassItems : (this.pass?.pass_items || this.pass?.passItems || []);
  }

  get currentAllowedSessionIds(): number[] | null {
    if (this.currentEventId == null) return null;
    return this.allowedSessionsByEvent.get(this.currentEventId) ?? null;
  }

  getEvent(item: any): any {
    return item.event || item;
  }

  isFlexible(): boolean {
    const minEvents = this.pass?.min_events;
    const total = this.getPassItems().length;
    return minEvents != null && minEvents < total;
  }

  getMinEvents(): number {
    return this.pass?.min_events || this.getPassItems().length;
  }

  getCompletedEventsCount(): number {
    let count = 0;
    for (const [, arr] of this.eventSelections) {
      if (arr.length >= this.quantity) count++;
    }
    return count;
  }

  isEventCompleted(eventId: number): boolean {
    return (this.eventSelections.get(eventId) || []).length >= this.quantity;
  }

  isCurrentEvent(eventId: number): boolean {
    return this.currentEventId === eventId;
  }

  getEventSelectionCount(eventId: number): number {
    return (this.eventSelections.get(eventId) || []).length;
  }

  hasMap(item: any): boolean {
    return !!(this.getEvent(item).use_map);
  }

  selectEvent(item: any) {
    const event = this.getEvent(item);
    this.router.navigate(['/pass-sale-bulk', this.passId, this.quantity, 'event', event.id]);
  }

  /** Abono de precio fijo por evento y nivel */
  isFixedPrices(): boolean {
    return isFixedPricePass(this.pass);
  }

  /** Total de lo elegido; con precio fijo es lo que costarán los abonos */
  getSelectionsTotal(): number {
    let total = 0;
    this.selectionDetails.forEach((details: any[]) => details.forEach((d: any) => {
      if (d.ticketPrice != null) total += +d.ticketPrice * (d.quantity || 1);
    }));
    return total;
  }

  onEventSelection(linePayload: any) {
    const eventId = linePayload.id_event;
    const display = linePayload._display || {};
    const { _display, ...payload } = linePayload;

    const currentSelections = this.eventSelections.get(eventId) || [];
    const currentDetails = this.selectionDetails.get(eventId) || [];
    const firstEventChosen = !Array.from(this.eventSelections.keys()).some(id => id !== eventId);

    // Only add if we still need more for this event
    if (currentSelections.length < this.quantity) {
      currentSelections.push({ ...payload, id_discount: this.pass.id });
      currentDetails.push({
        eventTitle: display.eventTitle || 'Evento',
        eventImage: display.eventImage || null,
        ticketName: display.ticketName || 'Entrada',
        ticketPrice: display.ticketPrice ?? null,
        quantity: payload.quantity || 1,
        sessionDate: display.sessionDate || null,
        seatLabel: display.seatLabel || null,
        seatSection: display.seatSection || null,
        seatRow: display.seatRow || null,
        seatSeat: display.seatSeat || null,
      });

      this.eventSelections.set(eventId, [...currentSelections]);
      this.selectionDetails.set(eventId, [...currentDetails]);
      // Force change detection
      this.eventSelections = new Map(this.eventSelections);
      this.selectionDetails = new Map(this.selectionDetails);
      this.refreshEventDetailsList();

      if (currentSelections.length >= this.quantity) {
        // Butacas del primer evento ya completas: si están libres en todo el
        // abono se ofrece venderlos enteros con ellas; si no (o si se
        // rechaza), se sigue evento a evento.
        const seatIds = currentSelections.map((s: any) => s.id_seat).filter((id: any) => !!id);
        if (firstEventChosen && seatIds.length === this.quantity && this.sameSeatOffer.canOffer(this.pass, this.getPassItems())) {
          this.sameSeatOffer.offerSameSeat(this.pass, eventId, seatIds).subscribe((quote) => {
            quote ? this.addSameSeatPasses(eventId, seatIds) : this.goToNextIncompleteEvent(eventId);
          });
          return;
        }

        this.goToNextIncompleteEvent(eventId);
      }
    }
  }

  /** Auto-navigate when all N slots for this event are filled, unless all pass requirements are already met */
  private goToNextIncompleteEvent(eventId: number) {
    if (!this.allEventsCompleted()) {
      const items = this.getPassItems();
      const nextItem = items.find((item: any) => {
        const eid = this.getEvent(item).id;
        return eid !== eventId && !this.isEventCompleted(eid);
      });
      if (nextItem) {
        setTimeout(() => this.selectEvent(nextItem), 500);
      }
    }
  }

  /**
   * Butaca única ofrecida y aceptada: los abonos entran en el carrito con las
   * butacas elegidas en `eventId` para todos sus eventos, una por abono. Si
   * entre medias alguna se ha ocupado, se quita la selección, se recarga el
   * mapa y se avisa; con cualquier otro fallo se sigue evento a evento.
   */
  private addSameSeatPasses(eventId: number, seatIds: number[]) {
    if (this.submitting) return;
    this.submitting = true;

    this.passesService.addSingleSeatPassToCart(this.pass.id, seatIds, eventId)
      .pipe(switchMap(() => this.cartService.getCart()))
      .subscribe({
        next: () => {
          this.submitting = false;
          this.passAdded = true;
        },
        error: (err) => {
          this.submitting = false;
          const seatsTaken = err?.status === 409 && err?.error?.errors?.code === 'seats_unavailable';
          if (seatsTaken) {
            this.removeSelection(eventId);
            this.seatMap?.getMapSession();
          } else {
            this.goToNextIncompleteEvent(eventId);
          }
          this.dialog.open(DialogGenericComponent, {
            data: {
              title: seatsTaken ? 'Butaca ya ocupada' : 'No se han podido añadir los abonos',
              subtitle: seatsTaken
                ? 'Alguna de las butacas acaba de ocuparse en uno de los eventos del abono. El mapa se ha actualizado: elige de nuevo.'
                : (err?.error?.errors?.errors?.[0] || err?.error?.message || 'Puedes seguir eligiendo evento a evento.'),
              icon: seatsTaken ? 'info' : 'error',
              buttons: [{ label: 'Cerrar', action: () => this.dialog.closeAll() }],
            },
          });
        }
      });
  }

  removeSelection(eventId: number) {
    if (eventId === this.currentEventId) {
      const selections = this.eventSelections.get(eventId) || [];
      const seatIds = selections.map((s: any) => s.id_seat).filter((id: any) => !!id);
      if (seatIds.length) this.seatMap?.clearPassSelections(seatIds);
    }
    this.eventSelections.delete(eventId);
    this.selectionDetails.delete(eventId);
    this.eventSelections = new Map(this.eventSelections);
    this.selectionDetails = new Map(this.selectionDetails);
    this.refreshEventDetailsList();
  }

  clearAllSelections() {
    if (this.currentEventId) {
      const selections = this.eventSelections.get(this.currentEventId) || [];
      const seatIds = selections.map((s: any) => s.id_seat).filter((id: any) => !!id);
      if (seatIds.length) this.seatMap?.clearPassSelections(seatIds);
    }
    this.eventSelections = new Map();
    this.selectionDetails = new Map();
    this.eventDetailsList = [];
  }

  getPreselectedSeatIds(eventId: number): number[] {
    const selections = this.eventSelections.get(eventId) || [];
    return selections.map((s: any) => s.id_seat).filter((id: any) => !!id);
  }

  getEventDetailsList(): { eventId: number; details: any[]; completed: boolean }[] {
    return this.eventDetailsList;
  }

  private refreshEventDetailsList() {
    this.eventDetailsList = Array.from(this.selectionDetails.entries()).map(([eventId, details]) => ({
      eventId,
      details,
      completed: this.isEventCompleted(eventId)
    }));
  }

  trackByEventId(_index: number, group: { eventId: number }): number {
    return group.eventId;
  }

  allEventsCompleted(): boolean {
    return this.getCompletedEventsCount() >= this.getMinEvents();
  }

  addPassToCart() {
    if (this.submitting || !this.allEventsCompleted()) return;
    this.submitting = true;

    // Build N sets of cart lines — one per "pass instance"
    const cartLinesPerPass: any[][] = [];
    for (let i = 0; i < this.quantity; i++) {
      const lines: any[] = [];
      for (const [, selections] of this.eventSelections) {
        if (selections[i]) lines.push(selections[i]);
      }
      cartLinesPerPass.push(lines);
    }

    // Chain N API calls sequentially
    let chain = of<any>(null);
    for (const lines of cartLinesPerPass) {
      chain = chain.pipe(switchMap(() => this.passesService.addPassToCart(this.pass.id, lines)));
    }

    chain.pipe(switchMap(() => this.cartService.getCart())).subscribe({
      next: () => {
        this.submitting = false;
        this.passAdded = true;
      },
      error: () => {
        this.submitting = false;
      }
    });
  }

  // --- Filtered pass items ---
  getFilteredPassItems(): any[] {
    let items = this.getPassItems();
    if (this.searchText?.trim()) {
      const term = this.searchText.trim().toLowerCase();
      items = items.filter((item: any) => {
        const event = this.getEvent(item);
        return event.title?.toLowerCase().includes(term);
      });
    }
    if (this.fromDate) {
      const from = new Date(this.formatter.format(this.fromDate));
      from.setHours(0, 0, 0, 0);
      const to = this.toDate ? new Date(this.formatter.format(this.toDate)) : new Date(from);
      to.setHours(23, 59, 59, 999);
      items = items.filter((item: any) => {
        const event = this.getEvent(item);
        const start = event.start_date ? new Date(event.start_date) : null;
        if (!start) return true;
        return start >= from && start <= to;
      });
    }
    return items;
  }

  toggleFilters() {
    this.filtersOpen = !this.filtersOpen;
    if (!this.filtersOpen) this.clearFilters();
  }

  onSearchChange(value: string) {
    this.searchText = value;
  }

  clearFilters() {
    this.searchText = '';
    this.fromDate = null;
    this.toDate = null;
    this.stringDate = '';
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText?.trim() || this.fromDate);
  }

  // --- Date picker ---
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent): void {
    if (this.openDatePicker && this.fechaDropdown &&
        !this.fechaDropdown.nativeElement.contains(event.target)) {
      this.openDatePicker = false;
    }
  }

  changeDate(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
      this.openDatePicker = false;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    const dateString = { min: this.formatter.format(this.fromDate), max: this.formatter.format(this.toDate) };
    let stringDates = this.formatStringDate(dateString.min)!;
    if (dateString.max && dateString.max !== '') {
      stringDates += ' - ' + this.formatStringDate(dateString.max);
    }
    this.stringDate = stringDates;
  }

  dateNavigate($event: NgbDatepickerNavigateEvent) {
    this.currentMonth = $event.next.month;
  }

  formatStringDate(date: any, format: string = 'dd MMM yy') {
    return date ? formatDate(new Date(date), format, 'es') : null;
  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate &&
      date.after(this.fromDate) && date.before(this.hoveredDate);
  }
  isMonday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getFirstDayWeek();
  isSunday = (date: NgbDate) => this.calendar.getWeekday(date) == this.getLastDayWeek();
  isWeekend = (date: NgbDate) => this.calendar.getWeekday(date) >= 6;
  isDisabled = (date: NgbDate) => !(date.equals(this.minDate) || (date.after(this.minDate) && date.month == this.currentMonth));
  isFromDate(date: NgbDate) { return this.fromDate ? date.equals(this.fromDate) : false; }
  isToDate(date: NgbDate) { return this.toDate ? date.equals(this.toDate) : false; }
  isInside(date: NgbDate) { return this.toDate && date.after(this.fromDate) && date.before(this.toDate); }
  isRange(date: NgbDate) {
    return (date.equals(this.fromDate) && this.toDate) ||
      (this.toDate && date.equals(this.toDate)) || this.isInside(date);
  }
  getFirstDayWeek() { return getLocaleFirstDayOfWeek('es'); }
  getLastDayWeek() {
    let first = getLocaleFirstDayOfWeek('es');
    let last = first + 6;
    if (last > 7) last = first - (last - 7);
    return last;
  }

  goBack() {
    this.router.navigate(['/passes']);
  }

  pay() {
    if (this.cartService.getCartQuantity() > 0) {
      this.ticketsEventService.checkCartBonds().subscribe({
        next: (checkBonds) => {
          if (checkBonds.success) {
            this.dialog.open(DialogPayComponent, {
              width: '500px',
              disableClose: false,
            });
          } else {
            this.openErrorBondsDialog(checkBonds);
          }
        }
      });
    }
  }

  openErrorBondsDialog(error: any) {
    const dialogPopup = new DialogInitializer(DialogErrorBondsComponent);
    dialogPopup.setConfig({
      width: '100%',
      layoutType: DialogLayoutDisplay.NONE,
      animationIn: AppearanceAnimation.FADE_IN,
      animationOut: DisappearanceAnimation.FADE_OUT,
    });
    dialogPopup.setCustomData({ error });
    dialogPopup.openDialog$().subscribe();
  }

  togglePanel(): void {
    this.panelCollapsed = !this.panelCollapsed;
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
    this.collapsedSub?.unsubscribe();
  }
}
